import { components } from "@appretio/api";
import { useMutation } from "@tanstack/react-query";
import type { AgGridReact } from "ag-grid-react";
import {
  Dispatch,
  MutableRefObject,
  RefObject,
  SetStateAction,
  useCallback
} from "react";
import {
  cutPastWindowEnd,
  extractTransactionalGridData
} from "../../components/cubeDataTable/utils/columnUtils";
import {
  extractIntakeRowsFromGrid,
  type IntakeCaches,
  type IntakeSource,
  isEditableIntakeSource
} from "../../components/cubeDataTable/utils/intakeUtils";
import { filterSaveableRows } from "../../utils/filterSaveableRows";
import {
  fillInOtbLocally,
  type TemporalGridRow
} from "../../utils/weekly/temporalGridAdapter";
import {
  type FetchForecastDataParams,
  type ForecastGridDataOutput
} from "../useFetchForecastData";
import { type ForecastMetaDataType } from "../useFetchForecastMetaData";
import { useSaveCubeData } from "../useSaveCubeData";
import { type CubeProducer } from "./useForecastWorkspace";
import { useHandleRestateFPOS } from "./useHandleRestateFPOS";
import type {
  AppliedCubeFilters,
  WorkspaceTableState
} from "./useWorkspaceTableState";

/** Options for {@link useWorkspaceActions}. */
interface UseWorkspaceActionsOptions {
  /** Producer scope (user or budget). */
  producer: CubeProducer;
  /** Ref to the AG Grid instance. */
  gridRef: RefObject<AgGridReact | null>;
  /** Metadata containing year mapping for grid-data extraction. */
  metaData: ForecastMetaDataType | undefined;
  /** Cube id to save edits to. ``undefined`` disables save. */
  saveCubeId: string | undefined;
  /** The most recent forecast data from the server. */
  forecastData: ForecastGridDataOutput | null;
  /** Filters used by the most recent Apply click. */
  appliedFilters: AppliedCubeFilters;
  /**
   * The intake source whose rows the grid shows (``useWorkspaceIntake``'s
   * ``gridSource``). Not the control's: after a failed swap the grid rolls
   * back while the control keeps the source it asked for.
   */
  gridSource: IntakeSource;
  /** Ref holding one intake cache per source not on the grid. */
  intakeCacheRef: MutableRefObject<IntakeCaches>;
  /** Set the saved-at timestamp. */
  setSavedAt: Dispatch<SetStateAction<string | null>>;
  /** Set the toast state. */
  setToastState: WorkspaceTableState["setToastState"];
  /** Set the restate-FPOS dialog open state. */
  setRestateFPOSDialogOpen: (open: boolean) => void;
  /** Imperative fetch for forecast data. */
  fetchForecastData: (
    params: FetchForecastDataParams
  ) => Promise<ForecastGridDataOutput | null>;
  /** Grid-state retention's capture for a transaction's rows (``GridStateRetention.rowSink``). */
  rowSink: () => (rows: TemporalGridRow[]) => void;
  /** Set the workspace's unsaved-edits flag. */
  setHasUnsavedEdits: Dispatch<SetStateAction<boolean>>;
  /** The workspace's unsaved-edits flag. */
  hasUnsavedEdits: boolean;
  /** Whether a cell write or intake swap is still recomputing the grid. */
  isRecomputing: boolean;
}

/**
 * Fill in OTB on the client: each open week's POTB bought as replenishment
 * (`fillInOtbLocally`), the rows cut at the save cube's window.
 */
function useLocalFillInOtb({
  producer,
  gridRef,
  metaData,
  forecastData,
  appliedFilters,
  rowSink,
  setHasUnsavedEdits
}: Pick<
  UseWorkspaceActionsOptions,
  | "producer"
  | "gridRef"
  | "metaData"
  | "forecastData"
  | "appliedFilters"
  | "rowSink"
  | "setHasUnsavedEdits"
>) {
  const { mutateAsync: fillInOtb, isPending } = useMutation({
    mutationFn: async (rows: TemporalGridRow[]) =>
      fillInOtbLocally(
        rows,
        producer,
        metaData,
        appliedFilters.context?.markdown_status,
        forecastData?.forecast_week_from
      ),
    meta: { errorMessage: "Failed to fill in OTB values" }
  });
  const cubeWindow = appliedFilters.saveCubeWindow;

  const handleFillInOtb = useCallback(async () => {
    const api = gridRef.current?.api;
    if (!api) return;
    const rows = extractTransactionalGridData(
      api,
      producer,
      metaData?.yearMapping
    );
    const captureRows = rowSink();
    try {
      const result = await fillInOtb(
        cutPastWindowEnd(rows, metaData, cubeWindow)
      );
      api.applyTransaction(result);
      captureRows(result.update);
      setHasUnsavedEdits(true);
    } catch {
      // The mutation's meta.errorMessage surfaces the toast.
    }
  }, [
    fillInOtb,
    producer,
    gridRef,
    rowSink,
    metaData,
    cubeWindow,
    setHasUnsavedEdits
  ]);
  return { handleFillInOtb, fillInOtbPending: isPending };
}

/** The grid API and cube a save writes, or a throw naming why it can't run. */
function saveTarget(
  gridRef: RefObject<AgGridReact | null>,
  saveCubeId: string | undefined,
  isRecomputing: boolean
) {
  const api = gridRef.current?.api;
  if (!api || !saveCubeId) {
    throw new Error("Nothing to save: no grid loaded from a writable cube.");
  }
  // Save is disabled mid-recompute; Save & apply is not, so it lands here.
  if (isRecomputing) {
    throw new Error("The grid is still recalculating. Try again in a moment.");
  }
  return { api, cubeId: saveCubeId };
}

/**
 * Action handlers for the forecast workspace: save, fill-in-OTB, and
 * restate-FPOS. Each handler extracts transactional data from the grid
 * and delegates to the appropriate mutation.
 *
 * Callbacks destructure the specific fields they read so their identity
 * only changes when those inputs change — depending on the whole options
 * bag would rebuild every callback on every render and defeat
 * ``useCallback``.
 */
export function useWorkspaceActions({
  producer,
  gridRef,
  metaData,
  saveCubeId,
  forecastData,
  appliedFilters,
  gridSource,
  intakeCacheRef,
  setSavedAt,
  setToastState,
  setRestateFPOSDialogOpen,
  fetchForecastData,
  rowSink,
  setHasUnsavedEdits,
  hasUnsavedEdits,
  isRecomputing
}: UseWorkspaceActionsOptions) {
  const yearMapping = metaData?.yearMapping;
  const { mutateAsync: saveCubeData, isPending: isSaving } = useSaveCubeData();
  const { handleRestateFPOS, restateFPOSPending } = useHandleRestateFPOS({
    saveCubeId,
    appliedFilters,
    fetchForecastData,
    tableState: { setRestateFPOSDialogOpen, setToastState }
  });

  const { handleFillInOtb, fillInOtbPending } = useLocalFillInOtb({
    producer,
    gridRef,
    metaData,
    forecastData,
    appliedFilters,
    rowSink,
    setHasUnsavedEdits
  });

  const saveGrid = useCallback(async () => {
    const { api, cubeId } = saveTarget(gridRef, saveCubeId, isRecomputing);
    if (isEditableIntakeSource(gridSource)) {
      intakeCacheRef.current[gridSource] = extractIntakeRowsFromGrid(api);
    }
    const data = filterSaveableRows(
      extractTransactionalGridData(api, producer, yearMapping),
      forecastData,
      gridSource
    );
    // Cleared before the await so an edit made while the save is in flight
    // stays unsaved.
    setHasUnsavedEdits(false);
    let result: Awaited<ReturnType<typeof saveCubeData>>;
    try {
      result = await saveCubeData({
        context:
          appliedFilters.context as components["schemas"]["ForecastContextOptions-Input"],
        cube_id: cubeId,
        transactional_grid_data: data
      });
    } catch (error) {
      if (hasUnsavedEdits) setHasUnsavedEdits(true);
      throw error;
    }
    setSavedAt(result.saved_at);
    setToastState({
      open: true,
      variant: "success",
      title: "Save successful",
      message: `Saved ${result.rows_affected} rows`
    });
  }, [
    gridRef,
    saveCubeId,
    gridSource,
    intakeCacheRef,
    producer,
    yearMapping,
    forecastData,
    appliedFilters,
    saveCubeData,
    setSavedAt,
    setToastState,
    setHasUnsavedEdits,
    hasUnsavedEdits,
    isRecomputing
  ]);

  const handleSave = useCallback(async () => {
    try {
      await saveGrid();
    } catch {
      // The mutation's meta.errorMessage surfaces the toast.
    }
  }, [saveGrid]);

  return {
    handleFillInOtb,
    handleRestateFPOS,
    handleSave,
    saveGrid,
    fillInOtbPending,
    isSaving,
    restateFPOSPending
  };
}
